import React, { useEffect } from 'react'
import { Form, Modal, notification, Select as AntSelect } from 'antd'
import {
  useGetInvoiceDeliveryGlobalSettingsQuery,
  useUpdateInvoiceDeliveryGlobalSettingsMutation,
} from '../../api/invoice-delivery-api-client'
import { validateEmailList } from '../helpers'
import {
  FormHint,
  FormLabel,
  ModalBody,
  ModalDescription,
} from './ClientBillingProfileManagement.styles'

interface GlobalCCEmailsModalProps {
  open: boolean
  onClose: () => void
}

const GlobalCCEmailsModal = ({ open, onClose }: GlobalCCEmailsModalProps) => {
  const [form] = Form.useForm()

  const { data: settings, isError: settingsError } =
    useGetInvoiceDeliveryGlobalSettingsQuery()

  const updateMutation = useUpdateInvoiceDeliveryGlobalSettingsMutation({
    onSuccess: () => {
      notification.success({ message: 'Global CC emails updated' })
      onClose()
    },
    onError: () => {
      notification.error({ message: 'Failed to update global CC emails' })
    },
  })

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ ccEmails: settings?.ccEmails ?? [] })
    }
    // Intentionally exclude `settings` — we only want to seed the form on
    // open, not on every background refetch which would overwrite unsaved edits.
  }, [open, form])

  const handleSave = async () => {
    if (settingsError) {
      notification.error({
        message: 'Cannot save — settings failed to load. Please refresh.',
      })
      return
    }
    try {
      const { ccEmails } = await form.validateFields()
      updateMutation.mutate({ ccEmails: ccEmails ?? [] })
    } catch {
      // antd displays field-level errors automatically
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Global CC emails"
      onOk={handleSave}
      okText="Save"
      confirmLoading={updateMutation.isPending}
      cancelButtonProps={{ disabled: updateMutation.isPending }}
      width={480}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
    >
      <ModalBody>
        <ModalDescription>
          These addresses are CC&apos;d on every invoice delivery email sent to
          clients.
        </ModalDescription>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="ccEmails"
            label={<FormLabel>CC email addresses</FormLabel>}
            rules={[{ validator: validateEmailList }]}
          >
            <AntSelect
              mode="tags"
              tokenSeparators={[',', ' ', ';']}
              placeholder="finance@company.com — press Enter or comma to add"
              open={false}
            />
          </Form.Item>
          <FormHint>
            Press Enter, comma, or semicolon after each address
          </FormHint>
        </Form>
      </ModalBody>
    </Modal>
  )
}

export { GlobalCCEmailsModal }
